import React, { useState } from 'react';

import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { successAlert, errorAlert, addOrganization } from 'actions';
import {
  OrganizationExtraDetailsState,
  buildExtraDetailsPayload,
  getExtraDetailsErrorMessage,
  saveOrganizationExtraDetails,
} from 'app/configuration/organizations/organization-extra-details';
import { OrganizationExtraDetailsModal } from 'app/configuration/organizations/organization-extra-details-modal';
import { OrganizationForm } from 'app/configuration/organizations/organization-form';
import { OrganizationFormState } from 'app/configuration/organizations/organization-helpers';
import { trimTrailingSlash } from 'helpers/url-helpers';

const initialState: OrganizationFormState = {
  englishName: '',
  arabicName: '',
};

const AddOrganization: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const baseURL = trimTrailingSlash(match.url);
  const intl = useIntl();

  // Set once the Ruby backend created the organisation: opens the
  // "additional details" modal (Express API).
  const [createdOrganization, setCreatedOrganization] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const goToOrganization = (organizationId: string) =>
    history.push(`${baseURL.replace('/new', '')}/${organizationId}`);

  const handleSubmit = (
    values: OrganizationFormState,
    { setSubmitting }: FormikHelpers<OrganizationFormState>
  ) => {
    setSubmitting(true);

    Promise.resolve(
      dispatch(
        addOrganization({
          en_name: values.englishName,
          ar_name: values.arabicName,
        })
      )
    )
      .then(({ action }: any) => {
        const newOrganizationId = action.payload.data?.response.organisation.id;

        dispatch(
          successAlert(intl.formatMessage({ id: 'successAddOrganization' }))
        );

        setCreatedOrganization({
          id: newOrganizationId,
          name: values.englishName,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSaveExtraDetails = async (
    values: OrganizationExtraDetailsState,
    { setSubmitting }: FormikHelpers<OrganizationExtraDetailsState>
  ) => {
    if (!createdOrganization) return;

    try {
      await saveOrganizationExtraDetails(
        createdOrganization.id,
        buildExtraDetailsPayload(values)
      );

      dispatch(
        successAlert(intl.formatMessage({ id: 'orgExtraDetailsSaved' }))
      );

      goToOrganization(createdOrganization.id);
    } catch (error) {
      // stay in the modal so the user can fix the values, retry or skip
      dispatch(errorAlert(getExtraDetailsErrorMessage(error)));
      setSubmitting(false);
    }
  };

  return (
    <>
      <OrganizationForm {...{ initialState, handleSubmit }} />

      <OrganizationExtraDetailsModal
        isOpen={Boolean(createdOrganization)}
        organizationName={createdOrganization?.name ?? ''}
        onSave={handleSaveExtraDetails}
        onSkip={() => createdOrganization && goToOrganization(createdOrganization.id)}
      />
    </>
  );
};

export { AddOrganization };
